import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Container, Paper, LinearProgress, Divider, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Card, CardContent, useMediaQuery,
  CircularProgress, Grid, FormControl, InputLabel, Select, MenuItem, TextField, InputAdornment,
  IconButton, Chip, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Snackbar, Alert, Tooltip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import BusinessIcon from '@mui/icons-material/Business';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import axios from 'axios';
import utils from '../../utils';

const Collaborazioni = () => {
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const clientiSectionRef = useRef(null);

  const [collaboratori, setCollaboratori] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [selectedCollaboratore, setSelectedCollaboratore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ragioniSociali, setRagioniSociali] = useState({});

  // Stati per la modifica della percentuale
  const [nuovaPercentuale, setNuovaPercentuale] = useState('');
  const [nuovoImportoPagato, setNuovoImportoPagato] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [clienteSelezionatoPerEdit, setClienteSelezionatoPerEdit] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    const fetchRagioniSociali = async () => {
      try {
        const res = await axios.get(`/api/aziendeeop`, {
          headers: utils.getAuthHeaders(),
        });
        const { success, error } = res.data;
        if (success) {
          const dizionario = {};
          for (const az of res.data.aziende) {
            dizionario[az.id] = az.nome;
          }
          setRagioniSociali(dizionario);
        } else {
          setError(error);
        }
      } catch (error) {
        setError(
          "Si è verificato un errore imprevisto sul nostro server."
        );
        console.log(error);
      }
    }
    fetchRagioniSociali();
    loadRequests();
  }, []);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/collaborazioni', {
        headers: utils.getAuthHeaders(),
      });
      const { success, error, clienti } = res.data;
      if (success) {
        const newCollaboratori = clienti.map(cli => {
          return {
            ...cli,
            numeroClientiPortati: cli.clienti_portati.length,
            importoTotaleGenerato: cli.clienti_portati.reduce((tot, cp) => tot + cp.importoTotale, 0),
            importoTotaleDovuto: cli.clienti_portati.reduce((tot, cp) => tot + cp.importoTotale * 0.01 * cp.percentualeCollaboratore, 0),
            importoTotalePagato: cli.clienti_portati.reduce((tot, cp) => tot + cp.importoPagato, 0),
          }
        });
        setCollaboratori(newCollaboratori);
        if (selectedCollaboratore) {
          setSelectedCollaboratore(newCollaboratori.filter(c => c.id == selectedCollaboratore.id)[0]);
        }
      } else {
        setSnackbarMessage(error);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage("Si è verificato un errore imprevisto sul nostro server.");
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      console.log(error);
    }
    setLoading(false);
  };

  // Effetto per scroll alla sezione clienti quando viene selezionato un collaboratore
  useEffect(() => {
    if (selectedCollaboratore) {
      setClienti(selectedCollaboratore.clienti_portati);
    }
    if (selectedCollaboratore && clientiSectionRef.current) {
      // Aggiungiamo un piccolo ritardo per dare tempo ai clienti di caricarsi
      setTimeout(() => {
        clientiSectionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 300);
    }
  }, [selectedCollaboratore]);

  const calculateProgress = (pagato, totale) => {
    return (pagato / (totale || 1)) * 100;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  // Funzioni per modificare la percentuale
  const handleOpenEditDialog = (cliente) => {
    setClienteSelezionatoPerEdit(cliente);
    setNuovaPercentuale(cliente.percentualeCollaboratore.toString());
    setNuovoImportoPagato(cliente.importoPagato);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setClienteSelezionatoPerEdit(null);
    setNuovoImportoPagato(0);
    setNuovaPercentuale('');
  };

  const handlePercentualeChange = (event) => {
    // Validazione: solo numeri
    const value = event.target.value;
    if (value === '' || (/^\d+$/.test(value) && parseInt(value) <= 100)) {
      setNuovaPercentuale(value);
    }
  };

  const handleSalvaPercentuale = async () => {
    if (!clienteSelezionatoPerEdit) return;
    try {
      const res = await axios.post(`/api/collaborazioni`,
        { collId: selectedCollaboratore.id, cliId: clienteSelezionatoPerEdit.id, pc: nuovaPercentuale, ip: nuovoImportoPagato }, {
        headers: utils.getAuthHeaders(),
      });
      const { success, error } = res.data;
      if (success) {
        loadRequests();
      } else {
        setSnackbarMessage(error);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error('Errore durante l\'aggiornamento:', err);
      setSnackbarMessage('Errore durante l\'aggiornamento dei dati');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    handleCloseDialog();
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const renderTableCollaboratori = () => {
    return (
      <TableContainer component={Paper} elevation={3}>
        <Table aria-label="tabella collaboratori">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#333333', color: '#FFFFFF' }}>
              <TableCell sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>Ragione Sociale</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>Clienti Portati</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>Importo Generato</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>Importo Dovuto</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>Importo Pagato</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>Stato Pagamenti</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {collaboratori.map((collaboratore) => {
              const progressPercentage = calculateProgress(collaboratore.importoTotalePagato, collaboratore.importoTotaleDovuto);
              return (
                <TableRow
                  key={collaboratore.id}
                  onClick={() => setSelectedCollaboratore(collaboratore)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                    backgroundColor: selectedCollaboratore?.id?.toString() === collaboratore.id.toString() ? 'rgba(25, 118, 210, 0.12)' : 'inherit'
                  }}
                >
                  <TableCell>{ragioniSociali[collaboratore.id]}</TableCell>
                  <TableCell>{collaboratore.numeroClientiPortati}</TableCell>
                  <TableCell>{formatCurrency(collaboratore.importoTotaleGenerato)}</TableCell>
                  <TableCell>{formatCurrency(collaboratore.importoTotaleDovuto)}</TableCell>
                  <TableCell>{formatCurrency(collaboratore.importoTotalePagato)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ width: '100%', mr: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={progressPercentage}
                          sx={{
                            height: 10,
                            borderRadius: 5,
                            backgroundColor: 'rgba(0, 0, 0, 0.1)',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: progressPercentage === 100 ? 'success.main' : 'primary.main',
                            }
                          }}
                        />
                      </Box>
                      <Box sx={{ minWidth: 35 }}>
                        <Typography variant="body2" color="text.secondary">
                          {`${Math.round(progressPercentage)}%`}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderCardsCollaboratori = () => {
    return (
      <Box>
        {collaboratori.map((collaboratore) => {
          const progressPercentage = calculateProgress(collaboratore.importoTotalePagato, collaboratore.importoTotaleDovuto);
          return (
            <Card
              key={collaboratore.id}
              sx={{
                mb: 2,
                cursor: 'pointer',
                border: selectedCollaboratore?.id?.toString() === collaboratore.id.toString() ? '2px solid #1976d2' : 'none'
              }}
              onClick={() => setSelectedCollaboratore(collaboratore)}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <BusinessIcon sx={{ mr: 1 }} />
                  <Typography variant="h6" component="div">
                    {ragioniSociali[collaboratore.id]}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Clienti Portati:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" fontWeight="bold">
                      {collaboratore.numeroClientiPortati}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Importo Generato:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(collaboratore.importoTotaleGenerato)}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Importo Dovuto:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(collaboratore.importoTotaleDovuto)}
                    </Typography>
                  </Grid>


                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Importo Pagato:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(collaboratore.importoTotalePagato)}
                    </Typography>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Stato Pagamenti:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={progressPercentage}
                        sx={{
                          height: 10,
                          borderRadius: 5,
                          backgroundColor: 'rgba(0, 0, 0, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: progressPercentage === 100 ? 'success.main' : 'primary.main',
                          }
                        }}
                      />
                    </Box>
                    <Box sx={{ minWidth: 35 }}>
                      <Typography variant="body2" color="text.secondary">
                        {`${Math.round(progressPercentage)}%`}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  };

  const renderTableClienti = () => {
    return (
      <TableContainer component={Paper} elevation={3}>
        <Table aria-label="tabella clienti portati">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#555555', color: '#FFFFFF' }}>
              <TableCell sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>Ragione Sociale</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>Data Inizio</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>% Riconosciuta</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>Importo Generato</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>Importo Dovuto</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>Importo Pagato</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>Stato Pagamento</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clienti.map((cliente) => {
              const progressPercentage = calculateProgress(cliente.importoPagato, cliente.importoTotale * 0.01 * cliente.percentualeCollaboratore);
              return (
                <TableRow key={cliente.id}>
                  <TableCell>{ragioniSociali[cliente.id]}</TableCell>
                  <TableCell>{new Date(cliente.ts).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Box
                      onClick={() => handleOpenEditDialog(cliente)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                          color: 'primary.main'
                        }
                      }}
                    >
                      {cliente.percentualeCollaboratore}%
                    </Box>
                  </TableCell>
                  <TableCell>{formatCurrency(cliente.importoTotale)}</TableCell>
                  <TableCell>{formatCurrency(cliente.importoTotale * 0.01 * cliente.percentualeCollaboratore)}</TableCell>
                  <TableCell>
                    <Box
                      onClick={() => handleOpenEditDialog(cliente)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                          color: 'primary.main',
                        },
                      }}
                    >
                      {formatCurrency(cliente.importoPagato)}
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ width: '100%', mr: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={progressPercentage}
                          sx={{
                            height: 10,
                            borderRadius: 5,
                            backgroundColor: 'rgba(0, 0, 0, 0.1)',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: progressPercentage === 100 ? 'success.main' : 'primary.main',
                            }
                          }}
                        />
                      </Box>
                      <Box sx={{ minWidth: 35 }}>
                        <Typography variant="body2" color="text.secondary">
                          {`${Math.round(progressPercentage)}%`}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderCardsClienti = () => {
    return (
      <Box>
        {clienti.map((cliente) => {
          const progressPercentage = calculateProgress(cliente.importoPagato, cliente.importoTotale * 0.01 * cliente.percentualeCollaboratore);
          return (
            <Card key={cliente.id} sx={{ mb: 2 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" component="div">
                    {ragioniSociali[cliente.id]}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Data inizio: {new Date(cliente.ts).toLocaleDateString()}
                </Typography>
                <Divider sx={{ my: 1 }} />

                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      % Riconosciuta:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      onClick={() => handleOpenEditDialog(cliente)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                          color: 'primary.main'
                        }
                      }}
                    >
                      {cliente.percentualeCollaboratore}%
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Importo Totale:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(cliente.importoTotale)}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Importo Dovuto:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      onClick={() => handleOpenEditDialog(cliente)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                          color: 'primary.main'
                        }
                      }}
                    >
                      {formatCurrency(cliente.importoTotale * 0.01 * cliente.percentualeCollaboratore)}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Importo Pagato:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(cliente.importoPagato)}
                    </Typography>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Stato Pagamento:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={progressPercentage}
                        sx={{
                          height: 10,
                          borderRadius: 5,
                          backgroundColor: 'rgba(0, 0, 0, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: progressPercentage === 100 ? 'success.main' : 'primary.main',
                          }
                        }}
                      />
                    </Box>
                    <Box sx={{ minWidth: 35 }}>
                      <Typography variant="body2" color="text.secondary">
                        {`${Math.round(progressPercentage)}%`}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
          Gestione Collaborazioni
        </Typography>

        <Typography variant="subtitle1" sx={{ fontStyle: 'italic', color: 'text.secondary', maxWidth: '100%', mx: 'auto' }}>
          Monitora tutte le aziende e i professionisti che collaborano con noi portando nuovi clienti.
          Consulta le percentuali riconosciute, gli importi generati e lo stato dei pagamenti.
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography variant="body1" color="error" sx={{ textAlign: 'center', py: 4 }}>
          {error}
        </Typography>
      ) : (
        <>
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 2 }}>
              Collaboratori
            </Typography>

            <Divider sx={{ mb: 2 }} />

            {collaboratori.length === 0 ? (
              <Typography variant="body1" sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                Non sono presenti collaboratori. Inizia ad aggiungerne uno!
              </Typography>
            ) : (
              isLgUp ? renderTableCollaboratori() : renderCardsCollaboratori()
            )}
          </Paper>

          {selectedCollaboratore && (
            <Paper ref={clientiSectionRef} elevation={3} sx={{ p: 3, scrollMarginTop: '20px' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
                  Clienti portati da: {ragioniSociali[selectedCollaboratore?.id]}
                </Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {clienti.length === 0 ? (
                <Typography variant="body1" sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  {'Questo collaboratore non ha ancora portato alcun cliente.'}
                </Typography>
              ) : (
                isLgUp ? renderTableClienti() : renderCardsClienti()
              )}
            </Paper>
          )}
        </>
      )}

      {/* Dialog per modifica percentuale */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Modifica dati cliente</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {clienteSelezionatoPerEdit && `Modifica la percentuale di collaborazione e l'importo pagato per ${ragioniSociali[clienteSelezionatoPerEdit.id]}`}
          </DialogContentText>

          <TextField
            autoFocus
            margin="dense"
            label="Percentuale (%)"
            type="number"
            fullWidth
            variant="outlined"
            value={nuovaPercentuale}
            onChange={handlePercentualeChange}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            inputProps={{ min: 0, max: 100 }}
            error={nuovaPercentuale !== '' && (parseInt(nuovaPercentuale) < 0 || parseInt(nuovaPercentuale) > 100)}
            helperText={
              nuovaPercentuale !== '' &&
                (parseInt(nuovaPercentuale) < 0 || parseInt(nuovaPercentuale) > 100)
                ? 'La percentuale deve essere compresa tra 0 e 100'
                : ''
            }
          />

          <TextField
            margin="dense"
            label="Importo Pagato (€)"
            type="number"
            fullWidth
            variant="outlined"
            value={nuovoImportoPagato}
            onChange={e => setNuovoImportoPagato(e.target.value)}
            InputProps={{
              endAdornment: <InputAdornment position="end">€</InputAdornment>,
            }}
            inputProps={{ min: 0 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="error" startIcon={<CancelIcon />}>
            Annulla
          </Button>
          <Button
            onClick={handleSalvaPercentuale}
            color="primary"
            startIcon={<SaveIcon />}
            disabled={
              nuovaPercentuale === '' ||
              parseInt(nuovaPercentuale) < 0 ||
              parseInt(nuovaPercentuale) > 100 ||
              nuovoImportoPagato === '' ||
              parseFloat(nuovoImportoPagato) < 0
            }
          >
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar per notifiche */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', mt: 4 }}>
        Per aggiungere un nuovo collaboratore, contatta l'amministratore di sistema.
      </Typography>
    </Container>
  );
};

export default Collaborazioni;
